import type {
  ExternalWorkIntegrationStatusValues,
  SyncType,
  WorkIntegrationSyncRequest,
} from '@/common/api/types/workIntegrations';
import { isWorkIntegrationSyncing } from '@/common/utils/workIntegrations';
import { useSyncWorkIntegrationMutation } from '@/features/settings/work-integrations/mutations/useSyncWorkIntegrationMutation';

export type SyncScope = Pick<WorkIntegrationSyncRequest, 'itemIds' | 'sourceItemIds'>;

type SyncOptions = Parameters<ReturnType<typeof useSyncWorkIntegrationMutation>['mutate']>[1];

interface UseScopedSyncArgs {
  workIntegrationId: number;
  integrationStatus: ExternalWorkIntegrationStatusValues | undefined;
  scope: SyncScope | null;
}

export const useScopedSync = ({
  workIntegrationId,
  integrationStatus,
  scope,
}: UseScopedSyncArgs) => {
  const { mutate, isPending, isSuccess } = useSyncWorkIntegrationMutation();

  const integrationIsSyncing = isWorkIntegrationSyncing(integrationStatus);

  const sync = (syncType: SyncType, options?: SyncOptions) => {
    if (scope === null) return;
    mutate({ workIntegrationId, payload: { syncType, ...scope } }, options);
  };

  return {
    sync,
    isScopeMissing: scope === null,
    integrationIsSyncing,
    isDisabled: scope === null || isPending || integrationIsSyncing,
    inProgress: isPending || (isSuccess && integrationIsSyncing),
  };
};
